import { useEffect, useMemo, useState } from 'react';
import { ChevronRight, Plus, Settings, X } from 'lucide-react';
import { db } from '@/lib/db';
import type { CourseHole, GroupRoundEnriched, Member } from '@/lib/db/types';
import { computeRoundSettlement, buildRoundPlayers, stakesFromGames } from '@/lib/games/engine/compute-round';
import { defaultStakeForFormat, stakeFromDbRow } from '@/lib/games/engine/stakes-helpers';
import { GAME_LABELS } from '@/lib/games/game-catalog';
import { ActiveRoundGamePanels, inferCurrentHole } from './ActiveRoundGamePanels';
import { MatchPressControls } from './MatchPressControls';
import { WolfLiveBoard } from './WolfLiveBoard';
import { standingsValueLabel } from '@/lib/display-name';
import { GroupScorecard } from '@/components/play/GroupScorecard';
import { Sheet } from '@/components/shared/Sheet';
import { AddGamesPrompt } from './AddGamesPrompt';
import { AddGamesFlow } from './AddGamesFlow';
import { JoinRequestsPanel } from './JoinRequestsPanel';
import { RoundSettingsSheet } from './RoundSettingsSheet';
import { ScorecardSkeleton } from './RoundLoadingSkeleton';
import { useRoundEngine } from './useRoundEngine';
import { removeRoundFromPlayTabCache } from '@/lib/play-tab-data';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';

interface Props {
  member: Member;
  roundId: string;
  onClose: () => void;
  onComplete: (roundId: string) => void;
}

function shouldShowGamesPrompt(round: GroupRoundEnriched): boolean {
  const hasGames = (round.games || []).some((g) => g.enabled);
  if (hasGames) return false;
  const mode = round.config?.game_mode as string | undefined;
  return mode !== 'casual' && mode !== 'games';
}

export function ActiveRoundSheet({ member, roundId, onClose, onComplete }: Props) {
  const [round, setRound] = useState<GroupRoundEnriched | null>(null);
  const [courseName, setCourseName] = useState('');
  const [teeMeta, setTeeMeta] = useState<{ slope?: number; yardage?: number }>({});
  const [prefetchedHoles, setPrefetchedHoles] = useState<CourseHole[]>([]);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(false);
  const [showPrompt, setShowPrompt] = useState(false);
  const [showAddGames, setShowAddGames] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [removingGame, setRemovingGame] = useState<string | null>(null);
  const [pendingRemoveFormat, setPendingRemoveFormat] = useState<string | null>(null);

  const load = async (opts?: { silent?: boolean }) => {
    if (!opts?.silent) setLoading(true);
    try {
      const data = await db.groupRounds.get(roundId);
      setRound(data);
      if (data) setShowPrompt(shouldShowGamesPrompt(data));
      if (!data?.course_id) {
        setPrefetchedHoles([]);
        return;
      }

      const [courses, tees, holes] = await Promise.all([
        db.courses.list(),
        data.tee_id ? db.courses.tees(data.course_id) : Promise.resolve([]),
        db.courses.holes(data.course_id),
      ]);
      setCourseName(courses.find((c) => c.id === data.course_id)?.name || '');
      if (data.tee_id) {
        const tee = tees.find((t) => t.id === data.tee_id);
        setTeeMeta({ slope: tee?.slope ?? undefined, yardage: tee?.yardage_total ?? undefined });
      }
      const sorted = [...holes].sort((a, b) => a.hole_num - b.hole_num);
      const scoped = data.holes === 'front-9'
        ? sorted.slice(0, 9)
        : data.holes === 'back-9'
          ? sorted.slice(9, 18)
          : sorted;
      setPrefetchedHoles(scoped);
    } finally {
      if (!opts?.silent) setLoading(false);
    }
  };

  useEffect(() => { void load(); }, [roundId]);

  useEffect(() => {
    if (!roundId) return;
    const unsub = db.groupRounds.subscribeScores(roundId, () => { void load({ silent: true }); });
    const poll = window.setInterval(() => { void load({ silent: true }); }, 8000);
    return () => {
      unsub();
      window.clearInterval(poll);
    };
  }, [roundId]);

  const engine = useRoundEngine(round, member, prefetchedHoles);

  const scorecardPlayers = useMemo(() => (round?.players || []).map((p) => ({
    id: p.member_id || p.player_key,
    name: p.display_name,
    handicap: p.handicap,
    you: p.member_id === member.id,
    guest: !p.member_id,
    avatar: p.avatar ?? (p.member_id === member.id ? member.avatar : null),
    profile_photo_url: p.profile_photo_url ?? (p.member_id === member.id ? member.profile_photo_url : null),
  })), [round?.players, member.id, member.avatar, member.profile_photo_url]);

  const scoresById = useMemo(() => {
    const map: Record<string, (number | null)[]> = {};
    (round?.players || []).forEach((p) => {
      const key = p.member_id || p.player_key;
      map[key] = engine.scoresByKey[p.player_key] || Array(18).fill(null);
    });
    return map;
  }, [round?.players, engine.scoresByKey]);

  const puttsById = useMemo(() => {
    const map: Record<string, (number | null)[]> = {};
    (round?.players || []).forEach((p) => {
      const key = p.member_id || p.player_key;
      map[key] = engine.puttsByKey[p.player_key] || Array(18).fill(null);
    });
    return map;
  }, [round?.players, engine.puttsByKey]);

  const activeGameIds = useMemo(
    () => (round?.games || []).filter((g) => g.enabled).map((g) => g.format_id),
    [round?.games],
  );

  const currentHole = useMemo(
    () => inferCurrentHole(engine.scoresByKey, (round?.players || []).map((p) => p.player_key)),
    [engine.scoresByKey, round?.players],
  );

  const hasWolf = activeGameIds.includes('wolf') || activeGameIds.includes('lone-wolf');
  const wolfRoundPlayers = useMemo(
    () => (round?.players ? buildRoundPlayers(round.players, member.id) : []),
    [round?.players, member.id],
  );
  const wolfStake = useMemo(() => {
    const g = round?.games?.find((x) => x.enabled && (x.format_id === 'wolf' || x.format_id === 'lone-wolf'));
    if (!g) return defaultStakeForFormat('wolf');
    return stakeFromDbRow(g.format_id, (g.stakes || {}) as Record<string, unknown>);
  }, [round?.games]);

  const canComplete = useMemo(() => {
    if (!round?.players?.length) return false;
    return round.players.every((p) => {
      const scores = engine.scoresByKey[p.player_key] || [];
      return scores.length > 0 && scores.every((s) => typeof s === 'number' && s > 0);
    });
  }, [round?.players, engine.scoresByKey]);

  const pressCtx = useMemo(() => {
    if (!round?.players?.length || !engine.holes.length) return null;
    return {
      holes: engine.holes,
      players: buildRoundPlayers(round.players, member.id),
      scores: engine.scoresByKey,
      stakes: stakesFromGames(round.games || []),
      teams: engine.teams,
    };
  }, [round?.players, member.id, engine.holes, engine.scoresByKey, round?.games, engine.teams]);

  useEffect(() => {
    if (!round || round.status !== 'active' || !pressCtx) return;
    const timer = window.setTimeout(() => {
      void db.groupRounds.syncPressStatuses(round.id, round.games || [], pressCtx)
        .then((changed) => { if (changed) void load({ silent: true }); });
    }, 400);
    return () => window.clearTimeout(timer);
  }, [round?.id, round?.status, round?.games, pressCtx, engine.scoresByKey]);

  const isOrganizer = round?.organizer_id === member.id;
  const isParticipant = (round?.players || []).some((p) => p.member_id === member.id);
  // Client decision: anyone in the round can enter/edit scores and manage games.
  const canEdit = isOrganizer || isParticipant;

  const hostLabel = useMemo(() => {
    if (!round) return '';
    if (round.organizer_id === member.id) return 'You';
    const host = round.players?.find((p) => p.is_organizer || p.member_id === round.organizer_id);
    return host?.display_name.split(' ')[0] ?? 'Host';
  }, [round, member.id]);

  const playersSummary = useMemo(() => {
    const names = (round?.players || []).map((p) => p.display_name.split(' ')[0]);
    if (!names.length) return 'No players yet';
    return names.join(', ');
  }, [round?.players]);

  const onScore = (playerId: string, holeIndex: number, val: number | null) => {
    if (!canEdit) return;
    const p = round?.players?.find((x) => (x.member_id || x.player_key) === playerId);
    if (!p || !round) return;
    void engine.setScore(p.player_key, holeIndex, val).then(() => {
      setRound((prev) => {
        if (!prev) return prev;
        const scores = [...(prev.scores || [])];
        const holeNum = holeIndex + 1;
        const ix = scores.findIndex((s) => s.player_key === p.player_key && s.hole_num === holeNum);
        const existing = ix >= 0 ? scores[ix] : null;
        const row = {
          group_round_id: prev.id,
          player_key: p.player_key,
          hole_num: holeNum,
          strokes: val,
          putts: existing?.putts ?? null,
          updated_at: new Date().toISOString(),
        };
        if (ix >= 0) scores[ix] = { ...scores[ix], ...row };
        else scores.push(row);
        return { ...prev, scores };
      });
    });
  };

  const onPutt = (playerId: string, holeIndex: number, val: number | null) => {
    if (!canEdit) return;
    const p = round?.players?.find((x) => (x.member_id || x.player_key) === playerId);
    if (!p || !round) return;
    void engine.setPutt(p.player_key, holeIndex, val).then(() => {
      setRound((prev) => {
        if (!prev) return prev;
        const scores = [...(prev.scores || [])];
        const holeNum = holeIndex + 1;
        const ix = scores.findIndex((s) => s.player_key === p.player_key && s.hole_num === holeNum);
        const existing = ix >= 0 ? scores[ix] : null;
        const row = {
          group_round_id: prev.id,
          player_key: p.player_key,
          hole_num: holeNum,
          strokes: existing?.strokes ?? null,
          putts: val,
          updated_at: new Date().toISOString(),
        };
        if (ix >= 0) scores[ix] = { ...scores[ix], ...row };
        else scores.push(row);
        return { ...prev, scores };
      });
    });
  };

  const handleSkipGames = async () => {
    if (!round) return;
    await db.groupRounds.update(round.id, {
      config: { ...round.config, game_mode: 'casual' },
    });
    setShowPrompt(false);
    void load();
  };

  const handleAddGamesOpen = () => {
    setShowPrompt(false);
    setShowAddGames(true);
  };

  const confirmRemoveGame = async (formatId: string) => {
    if (!round) return;
    setRemovingGame(formatId);
    try {
      const kept = (round.games || [])
        .filter((g) => g.enabled && g.format_id !== formatId)
        .map((g) => ({
          format_id: g.format_id,
          enabled: true,
          stakes: g.stakes as Record<string, unknown>,
          config: g.config as Record<string, unknown>,
        }));
      await db.groupRounds.setGames(round.id, kept);
      if (!kept.length) {
        await db.groupRounds.update(round.id, {
          config: { ...round.config, game_mode: 'casual' },
        });
      }
      setPendingRemoveFormat(null);
      void load();
    } finally {
      setRemovingGame(null);
    }
  };

  const handleComplete = async () => {
    if (!round?.players) return;
    setCompleting(true);
    try {
      if (pressCtx && round.games?.length) {
        await db.groupRounds.syncPressStatuses(round.id, round.games, pressCtx);
      }
      const scoreRows = (round.scores || []).map((s) => ({
        player_key: s.player_key,
        hole_num: s.hole_num,
        strokes: s.strokes,
        putts: s.putts ?? null,
      }));

      if (engine.isCasual) {
        const strokeOnly = engine.gameResults;
        await db.groupRounds.complete(roundId, {
          net_by_player: {},
          simplified_payments: [],
          game_results: strokeOnly as unknown as Record<string, unknown>[],
        });
      } else {
        const settlement = computeRoundSettlement(
          engine.holes,
          round.players,
          scoreRows,
          round.games || [],
          member.id,
          round.config?.teams as string[][] | undefined,
        );
        await db.groupRounds.complete(roundId, {
          net_by_player: settlement.net_by_player,
          simplified_payments: settlement.simplified_payments,
          game_results: settlement.gameResults as unknown as Record<string, unknown>[],
        });
      }
      onComplete(roundId);
    } finally {
      setCompleting(false);
    }
  };

  if (!round && loading) {
    return (
      <Sheet title="Round" onClose={onClose}>
        <div className="space-y-4 px-4 pb-8 pt-2">
          <div className="h-4 w-48 animate-pulse rounded bg-line" />
          <ScorecardSkeleton />
        </div>
      </Sheet>
    );
  }

  if (!round) {
    return <Sheet title="Round" onClose={onClose}><div className="p-4 text-sm text-mute">Round not found.</div></Sheet>;
  }

  const standingsLabel = engine.isCasual ? 'LEADERBOARD' : 'LIVE STANDINGS';

  const gamesTopBanner = !showAddGames ? (
    <button
      type="button"
      onClick={() => setShowAddGames(true)}
      className="mb-3 flex w-full items-center gap-3 rounded-xl border-2 border-navy bg-navy p-4 text-left shadow-bt"
    >
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gold text-xl text-navy">
        <Plus size={22} strokeWidth={2.5} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-serif text-base font-bold text-cream">
          {activeGameIds.length ? 'Games on this round' : 'Add Games'}
        </div>
        {activeGameIds.length > 0 ? (
          <div className="mt-0.5 truncate text-xs text-cream/75">
            {activeGameIds.map((id) => GAME_LABELS[id] || id).join(' · ')}
            <span className="text-cream/55"> — tap to add more</span>
          </div>
        ) : (
          <div className="mt-0.5 text-xs leading-relaxed text-cream/75">
            Skins, Nassau, Stableford — pick more than one, or keep scoring only
          </div>
        )}
      </div>
      <ChevronRight size={18} className="shrink-0 text-cream/70" />
    </button>
  ) : null;

  const settingsButton = (
    <button
      type="button"
      onClick={() => setShowSettings(true)}
      className="flex h-9 w-9 items-center justify-center rounded-[10px] bg-cream text-ink-soft"
      title="Round settings"
    >
      <Settings size={17} />
    </button>
  );

  return (
    <>
      <Sheet title={courseName || 'Group Round'} onClose={onClose} headerRight={settingsButton}>
        <div className="space-y-4 px-4 pb-8 pt-2">
          <div className="rounded-xl border border-line bg-paper p-3">
            <div className="text-xs text-ink-soft">
              {isOrganizer ? 'You created this round' : `Hosted by ${hostLabel}`}
            </div>
            <div className="mt-1 text-xs text-mute">
              {(round.players?.length ?? 0)} player{(round.players?.length ?? 0) === 1 ? '' : 's'}
              {playersSummary ? ` · ${playersSummary}` : ''}
            </div>
          </div>

          <p className="text-xs text-ink-soft">
            {engine.isCasual
              ? 'Tap a hole to enter strokes — everyone in the group can keep score.'
              : 'Tap a hole to enter scores — every game updates automatically.'}
          </p>

          {!canEdit && (
            <div className="rounded-lg border border-gold/40 bg-gold/10 px-3 py-2 text-xs font-semibold text-gold-deep">
              Live scoreboard — scores update as players enter them.
            </div>
          )}

          <JoinRequestsPanel
            roundId={roundId}
            enabled={isOrganizer && Boolean(round?.config?.is_joinable)}
            onChanged={() => void load({ silent: true })}
          />

          {engine.holes.length > 0 ? (
            <GroupScorecard
              holes={engine.holes}
              teeSlope={teeMeta.slope}
              teeYardageTotal={teeMeta.yardage}
              players={scorecardPlayers}
              scoresById={scoresById}
              puttsById={puttsById}
              onScore={onScore}
              onPutt={onPutt}
              readOnly={!canEdit}
              topBanner={gamesTopBanner}
              showGpsCaddy={false}
            />
          ) : (
            <ScorecardSkeleton />
          )}

          {activeGameIds.length > 0 && (
            <div>
              <div className="mb-2 text-[10px] font-extrabold tracking-wide text-gold-deep">GAMES ON ROUND</div>
              <div className="flex flex-wrap gap-2">
                {activeGameIds.map((id) => (
                  <div
                    key={id}
                    className="flex items-center gap-1 rounded-full border border-line bg-paper py-1 pl-3 pr-1 text-[11px] font-semibold text-ink"
                  >
                    {GAME_LABELS[id] || id}
                    {canEdit && (
                      <button
                        type="button"
                        disabled={removingGame === id}
                        onClick={() => setPendingRemoveFormat(id)}
                        className="flex h-6 w-6 items-center justify-center rounded-full text-mute hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                        aria-label={`Remove ${id}`}
                      >
                        <X size={12} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <ActiveRoundGamePanels
            round={round}
            member={member}
            activeGameIds={activeGameIds}
            currentHole={currentHole}
            onConfigUpdated={() => { void load(); }}
          />

          {round && engine.holes.length > 0 && (
            <MatchPressControls
              round={round}
              member={member}
              holes={engine.holes}
              scoresByKey={engine.scoresByKey}
              teams={engine.teams}
              currentHoleIdx={currentHole}
              canEdit={canEdit}
              onUpdated={() => { void load({ silent: true }); }}
            />
          )}

          {hasWolf && round && engine.holes.length > 0 && (
            <WolfLiveBoard
              round={round}
              players={wolfRoundPlayers}
              holes={engine.holes}
              scoresByKey={engine.scoresByKey}
              stake={wolfStake}
              currentHole={currentHole}
            />
          )}

          {engine.teams && engine.teams.length >= 2 && (
            <div>
              <div className="mb-2 text-[10px] font-extrabold tracking-wide text-gold-deep">TEAMS</div>
              <div className="grid grid-cols-2 gap-2">
                {engine.teams.map((teamKeys, idx) => (
                  <div key={idx} className="rounded-lg border border-line bg-paper px-3 py-2">
                    <div className="mb-1 text-[10px] font-bold uppercase text-mute">Team {idx === 0 ? 'A' : 'B'}</div>
                    <div className="space-y-0.5">
                      {teamKeys.map((key) => (
                        <div key={key} className="text-xs font-semibold text-navy">{engine.playerName(key)}</div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            <div className="mb-2 text-[10px] font-extrabold tracking-wide text-gold-deep">{standingsLabel}</div>
            <div className="space-y-2">
              {engine.gameResults.length === 0 ? (
                <div className="text-xs text-mute">Enter scores to see the leaderboard.</div>
              ) : engine.gameResults.map((g) => (
                <div key={g.formatId} className="rounded-lg border border-line bg-paper px-3 py-2.5">
                  <div className="text-[11px] font-bold text-navy">{g.label}</div>
                  <div className="text-xs text-ink-soft">{g.summary}</div>
                  {g.standings && g.standings.length > 0 && (
                    <div className="mt-2 space-y-1">
                      <div className="flex justify-between text-[9px] font-bold uppercase tracking-wide text-mute">
                        <span>Player</span>
                        <span>{standingsValueLabel(g.formatId)}</span>
                      </div>
                      {g.standings.slice(0, 4).map((s) => (
                        <div key={s.playerKey} className="flex justify-between text-[11px]">
                          <span className="text-ink">{s.rank}. {s.name}</span>
                          <span className="font-semibold text-navy">{s.value}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {engine.balanceLines.length > 0 && !engine.isCasual && (
            <div className="rounded-xl bg-navy px-3.5 py-3">
              <div className="mb-2 text-[11px] font-extrabold tracking-wide text-gold-bright">RUNNING BALANCES</div>
              {engine.balanceLines.map((line) => (
                <div key={line.playerKey} className="flex justify-between py-1 text-xs">
                  <span className={line.you ? 'font-bold text-gold' : 'text-cream'}>{line.name}</span>
                  <span className={line.cents > 0 ? 'text-green-300' : 'text-red-300'}>
                    {line.cents > 0 ? '+' : '−'}${(Math.abs(line.cents) / 100).toFixed(0)}
                  </span>
                </div>
              ))}
              <p className="mt-1 text-[10px] text-white/50">Settle off-app — estimates only.</p>
            </div>
          )}

          <button
            type="button"
            disabled={completing || !canComplete}
            onClick={() => void handleComplete()}
            className="w-full rounded-xl bg-gold py-3.5 text-[15px] font-extrabold text-navy disabled:opacity-60"
          >
            {completing ? 'Finishing…' : 'Complete Round'}
          </button>
        </div>
      </Sheet>

      {showPrompt && (
        <AddGamesPrompt
          onSkip={() => void handleSkipGames()}
          onAddGames={handleAddGamesOpen}
        />
      )}

      {showAddGames && (
        <AddGamesFlow
          round={round}
          member={member}
          onClose={() => {
            setShowAddGames(false);
            void load();
          }}
          onSaved={() => {
            setShowAddGames(false);
            void load();
          }}
        />
      )}

      {showSettings && (
        <RoundSettingsSheet
          round={round}
          member={member}
          onClose={() => setShowSettings(false)}
          onUpdated={() => { setShowSettings(false); void load(); }}
          onCancelled={() => { removeRoundFromPlayTabCache(roundId); setShowSettings(false); onClose(); }}
        />
      )}

      <ConfirmDialog
        open={pendingRemoveFormat != null}
        title={`Remove ${pendingRemoveFormat ? (GAME_LABELS[pendingRemoveFormat] || pendingRemoveFormat) : 'game'}?`}
        message="This game will be removed from the round."
        confirmLabel="Remove"
        destructive
        busy={removingGame === pendingRemoveFormat}
        onConfirm={() => pendingRemoveFormat && void confirmRemoveGame(pendingRemoveFormat)}
        onCancel={() => setPendingRemoveFormat(null)}
      />
    </>
  );
}
